import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import cls from 'clsx';
import styles from './message.module.css';
import { IToastProps } from './Message.types';
import Info from '@/assets/icons/info.svg?react';
import Success from '@/assets/icons/success.svg?react';
import Error from '@/assets/icons/error.svg?react';
import Warning from '@/assets/icons/warning.svg?react';
import Loading from '@/assets/icons/loading.svg?react';
import Close from '@/assets/icons/close.svg?react';

const typeToIcon = {
  info: Info,
  success: Success,
  error: Error,
  warning: Warning,
  loading: Loading
};

const Toast = forwardRef<HTMLDivElement, IToastProps>(
  ({ content, type, duration, remove, toastId }, ref) => {
    const IconComponent = typeToIcon[type];
    const onClose = useCallback(() => {
      remove(toastId);
    }, [remove, toastId]);

    useEffect(() => {
      let timer: ReturnType<typeof setTimeout> | null = null;

      if (duration) {
        timer = setTimeout(onClose, duration * 1000);
      }

      return () => {
        if (timer) clearTimeout(timer);
      };
    }, [duration, onClose]);

    const canClose = useMemo(() => duration === 0 || duration > 5, [duration]);

    return (
      <div role="alert" className={styles.toast} ref={ref}>
        {IconComponent && (
          <div className={styles.iconWrapper}>
            <IconComponent className={cls(styles.icon, styles[type])} />
          </div>
        )}
        <span className={styles.text}>{content}</span>
        {canClose && (
          <button onClick={onClose} className={styles.close}>
            <Close className={styles.closeIcon} />
          </button>
        )}
      </div>
    );
  }
);

Toast.displayName = 'Toast';

export default memo(Toast);
